/**
 * Results of a backtest / comparison run: the strategy table, cumulative P&L and drawdown
 * per strategy, and for the focused strategy its metrics, P&L by cycle, path regimes and the
 * cycle list (each cycle opens the replay). Engine values are USD; shown in ₹ at the run's rate.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ReplayIcon from '@mui/icons-material/Replay';
import { Bar, BarChart, CartesianGrid, Cell, Line, LineChart, ReferenceLine, Tooltip, XAxis, YAxis } from 'recharts';
import toast from 'react-hot-toast';
import { optionsimService, simError, SimCycle, SimRunRow, SimSubRun } from '../../services/optionsim.service';
import { ChartCard, SeriesKey, Stat, StatGrid, seriesColor, useChartChrome, useIsPhone } from './simUi';
import { REGIME_LABEL, UTIL_HINT, axisDate, dateTimeUtc, dateUtc, exhaustedText, inrShort, money, num, pct, signedMoney, utilPct } from './simFormat';

const MAX_POINTS = 480;
const slotOf = (key: string) => Number(key.split(':')[0]) || 0;
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Every part of one artifact kind (parts are ≤ 1.5 MB gzip each). */
async function allParts<T>(runId: number, kind: string): Promise<T[]> {
  const out: T[] = [];
  for (let part = 0, parts = 1; part < parts && part < 200; part++) {
    const r = await optionsimService.artifact<T>(runId, kind, { part });
    parts = r.parts;
    out.push(...r.items);
  }
  return out;
}

function closeLabel(c: SimCycle['summary']): string {
  if (c.stopped) return `stopped${c.closeReason ? `: ${c.closeReason}` : ''}`;
  if (c.closeReason) return c.closeReason;
  return 'held to settlement';
}

export const SimResults: React.FC<{ run: SimRunRow; onReplay: (key: string, cycle: number, label: string) => void }> = ({ run, onReplay }) => {
  const phone = useIsPhone();
  const { C, axis, grid, tooltip } = useChartChrome();
  const cfg = run.config || {};
  const fx: number = cfg.usdInr ?? 85;
  const capitalUsd = (cfg.capitalInr ?? 250000) / fx;
  const subs: SimSubRun[] = useMemo(() => run.summary?.runs ?? [], [run.summary]);
  const scenarios = useMemo(() => Array.from(new Set(subs.map(s => s.costScenario))), [subs]);
  const [scenario, setScenario] = useState<'base' | 'stressed'>(scenarios.includes('base') ? 'base' : (scenarios[0] ?? 'base'));
  const visible = useMemo(() => subs.filter(s => s.costScenario === scenario), [subs, scenario]);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const focus = visible.find(s => s.key === focusKey) ?? visible[0] ?? null;

  const [equity, setEquity] = useState<Record<string, Array<[number, number]>>>({});
  const [cycles, setCycles] = useState<Record<string, SimCycle[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    setLoading(true); setLoadErr(null); setEquity({}); setCycles({});
    (async () => {
      try {
        const [eq, cy] = await Promise.all([
          allParts<{ key: string; points: Array<[number, number]> }>(run.id, 'equity'),
          allParts<SimCycle>(run.id, 'cycles'),
        ]);
        if (dead) return;
        const e: Record<string, Array<[number, number]>> = {};
        for (const x of eq) e[x.key] = x.points;
        const c: Record<string, SimCycle[]> = {};
        for (const x of cy) (c[x.key] ??= []).push(x);
        for (const k of Object.keys(c)) c[k].sort((a, b) => a.cycle - b.cycle);
        setEquity(e); setCycles(c);
      } catch (err: any) {
        if (!dead) setLoadErr(simError(err));
      } finally { if (!dead) setLoading(false); }
    })();
    return () => { dead = true; };
  }, [run.id]);

  // cumulative P&L (₹) per visible strategy on one time axis, down-sampled alike
  const chart = useMemo(() => {
    const series = visible.filter(s => equity[s.key]?.length);
    if (!series.length) return { rows: [] as any[], span: 0 };
    const len = Math.max(...series.map(s => equity[s.key].length));
    const step = Math.max(1, Math.ceil(len / MAX_POINTS));
    const base = series.find(s => equity[s.key].length === len)!;
    const maps = series.map(s => new Map(equity[s.key].map(([t, v]) => [t, v])));
    const peaks = series.map(() => -Infinity);
    const worst = series.map(() => ({ dd: 0, i: -1 }));
    const all: any[] = [];
    const pts = equity[base.key];
    for (let i = 0; i < pts.length; i++) {
      const t = pts[i][0];
      const row: any = { t };
      series.forEach((s, j) => {
        const v = maps[j].get(t);
        if (v == null) return;
        const cum = (v - capitalUsd) * fx;
        peaks[j] = Math.max(peaks[j], cum);
        const dd = Math.min(0, cum - peaks[j]);
        if (dd < worst[j].dd) worst[j] = { dd, i };
        row[`p${slotOf(s.key)}`] = Math.round(cum);
        row[`d${slotOf(s.key)}`] = Math.round(dd);
      });
      all.push(row);
    }
    // every step-th row, the last, and each series' deepest-drawdown row (so the chart shows
    // the same max drawdown as the table)
    const keep = new Set(worst.map(w => w.i).filter(i => i >= 0));
    const rows = all.filter((_, i) => i % step === 0 || i === all.length - 1 || keep.has(i));
    return { rows, span: pts[pts.length - 1][0] - pts[0][0] };
  }, [visible, equity, capitalUsd, fx]);

  const focusCycles = useMemo(() => (focus ? cycles[focus.key] ?? [] : []), [focus, cycles]);
  const bars = useMemo(() => focusCycles.map(c => ({ cycle: c.cycle, t: c.summary.entryT, pnl: Math.round(c.summary.pnlUsd * fx) })), [focusCycles, fx]);

  const exportCsv = async () => {
    try {
      const blob = await optionsimService.exportCsv(run.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `optionsim-run-${run.id}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e: any) { toast.error(`Export failed: ${simError(e)}`); }
  };

  const entryText = cfg.entry?.kind === 'daily' ? `daily ${cfg.entry?.hhmmUtc}` : `${WEEKDAYS[cfg.entry?.weekdayUtc ?? 5]} ${cfg.entry?.hhmmUtc ?? ''}`;
  const tone = (v: number) => (v > 0 ? C.bull : v < 0 ? C.bear : undefined);
  const notes = Array.from(new Set(subs.map(s => s.dataNote).filter(Boolean))) as string[];
  const ex = focus?.execVersion;
  const m = focus?.metrics;

  const legend = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 0.75 }}>
      {visible.map(s => <SeriesKey key={s.key} color={seriesColor(C.chartSeries, slotOf(s.key))} label={s.label} />)}
    </Box>
  );

  return (
    <Stack spacing={2} data-testid="optionsim-results">
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Typography variant="h6" fontWeight={800} sx={{ overflowWrap: 'break-word', minWidth: 0, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>{run.name || `Run ${run.id}`}</Typography>
            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} sx={{ flexShrink: 0 }}>Fills CSV</Button>
          </Stack>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
            {cfg.from != null && <Chip size="small" variant="outlined" label={`${dateUtc(cfg.from)} → ${dateUtc(cfg.to - 86400)}`} />}
            <Chip size="small" variant="outlined" label={`${cfg.res === 60 ? '1m' : '5m'} bars · decide every ${num((cfg.cadenceSec ?? 300) / 60, 0)} min`} />
            <Chip size="small" variant="outlined" label={`entry ${entryText} UTC`} />
            <Chip size="small" variant="outlined" label={`${num(cfg.contractsPerLeg, 0)} contracts / leg`} />
            <Chip size="small" variant="outlined" label={`capital ${money(cfg.capitalInr ?? 0, 1)}`} />
            <Chip size="small" variant="outlined" label={cfg.marginMode === 'PM' ? 'PM margin [A]' : 'Cross margin'} />
          </Box>
          {run.status === 'CANCELLED' && <Alert severity="warning" sx={{ mt: 1.5 }}>Cancelled: only the strategies finished before the cancel are shown.</Alert>}
          {notes.map(n => <Alert key={n} severity="warning" sx={{ mt: 1.5 }}>{n}</Alert>)}
          {scenarios.length > 1 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.5 }}>
              <Typography variant="caption" fontWeight={700}>Costs</Typography>
              <ToggleButtonGroup size="small" exclusive value={scenario} onChange={(_, v) => v && setScenario(v)} data-testid="optionsim-scenario">
                <ToggleButton value="base">Base</ToggleButton>
                <ToggleButton value="stressed">Stressed</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ── strategy comparison ── */}
      <Card>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={800} gutterBottom>Strategies · {scenario === 'base' ? 'base' : 'stressed (2× spreads)'} costs</Typography>
          {phone ? (
            <Stack spacing={1} data-testid="optionsim-strategy-cards">
              {visible.map(s => (
                <Box key={s.key} onClick={() => setFocusKey(s.key)}
                  sx={{ p: 1, borderRadius: 1, border: '1px solid', borderColor: focus?.key === s.key ? 'primary.main' : 'divider', cursor: 'pointer' }}>
                  <SeriesKey color={seriesColor(C.chartSeries, slotOf(s.key))} label={s.label} />
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.5, mt: 0.75 }}>
                    <MiniStat k="Total" v={signedMoney(s.metrics.totalNetUsd, fx)} color={tone(s.metrics.totalNetUsd)} />
                    <MiniStat k="Cycles" v={num(s.metrics.n, 0)} />
                    <MiniStat k="Win" v={pct(s.metrics.winRatePct, 0)} />
                    <MiniStat k="ES99" v={signedMoney(s.metrics.es99Usd, fx)} color={tone(s.metrics.es99Usd)} />
                    <MiniStat k="Max DD" v={signedMoney(s.metrics.maxDrawdownUsd, fx)} color={tone(s.metrics.maxDrawdownUsd)} />
                    <MiniStat k="Return" v={pct(s.metrics.returnOnCapitalPct, 1)} />
                  </Box>
                  {!!s.metrics.capitalExhaustedCycles && (
                    <Typography variant="caption" sx={{ color: C.warn, display: 'block', mt: 0.5 }}>
                      Capital exhausted from cycle {s.metrics.firstExhaustedCycle ?? '?'} (no liquidation modelled)
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" data-testid="optionsim-strategy-table" sx={{ '& td, & th': { px: 1, py: 0.6, whiteSpace: 'nowrap', fontSize: '0.78rem' }, '& td': { fontVariantNumeric: 'tabular-nums' } }}>
                <TableHead>
                  <TableRow>
                    {([
                      ['Strategy', 'Click a row to focus it below.'], ['Cycles', 'Baskets opened.'], ['Total net', 'Sum of cycle P&L after fees and modelled slippage.'],
                      ['Mean / cycle', 'Hover a value for its bootstrap 95% interval.'], ['Win', 'Cycles with P&L > 0.'], ['ES99', 'Average of the worst 1% of cycles (at least one).'],
                      ['Max DD', 'Largest fall of cumulative P&L from a peak (marked hourly).'],
                      ['Adj / cycle', 'Counted adjustments per cycle.'], ['Max margin', UTIL_HINT], ['Return', 'Total net / capital.'],
                    ] as Array<[string, string]>).map(([h, hint]) => (
                      <MuiTooltip key={h} title={hint} arrow><TableCell sx={{ fontWeight: 700 }} align={h === 'Strategy' ? 'left' : 'right'}>{h}</TableCell></MuiTooltip>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visible.map(s => {
                    const x = s.metrics;
                    return (
                      <TableRow key={s.key} hover selected={focus?.key === s.key} onClick={() => setFocusKey(s.key)} sx={{ cursor: 'pointer' }}>
                        <TableCell sx={{ whiteSpace: 'normal !important', minWidth: 200 }}><SeriesKey color={seriesColor(C.chartSeries, slotOf(s.key))} label={s.label} wrap /></TableCell>
                        <TableCell align="right">{num(x.n, 0)}</TableCell>
                        <TableCell align="right" sx={{ color: tone(x.totalNetUsd), fontWeight: 800 }}>{signedMoney(x.totalNetUsd, fx)}</TableCell>
                        <MuiTooltip arrow title={x.meanCi95 ? `95% interval (bootstrap): ${signedMoney(x.meanCi95[0], fx)} to ${signedMoney(x.meanCi95[1], fx)}` : 'Too few cycles for an interval'}>
                          <TableCell align="right">{signedMoney(x.meanUsd, fx)}</TableCell>
                        </MuiTooltip>
                        <TableCell align="right">{pct(x.winRatePct, 0)}</TableCell>
                        <TableCell align="right" sx={{ color: tone(x.es99Usd) }}>{signedMoney(x.es99Usd, fx)}</TableCell>
                        <TableCell align="right" sx={{ color: tone(x.maxDrawdownUsd) }}>{signedMoney(x.maxDrawdownUsd, fx)}</TableCell>
                        <TableCell align="right">{num(x.adjustmentsPerCycle, 1)}</TableCell>
                        {x.capitalExhaustedCycles ? (
                          <MuiTooltip arrow title={exhaustedText(x.capitalExhaustedCycles, x.n, x.firstExhaustedCycle)}>
                            <TableCell align="right" sx={{ color: C.warn, fontWeight: 700 }}>exhausted</TableCell>
                          </MuiTooltip>
                        ) : <TableCell align="right">{utilPct(x.maxMarginUtilPct)}</TableCell>}
                        <TableCell align="right">{pct(x.returnOnCapitalPct, 1)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
          {!visible.length && <Typography variant="body2" color="text.secondary">No strategy finished in this run.</Typography>}
        </CardContent>
      </Card>

      {loading && <LinearProgress />}
      {loadErr && <Alert severity="error">Could not load the run's series: {loadErr}</Alert>}

      {!loading && chart.rows.length > 0 && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' } }}>
          <ChartCard title="Cumulative P&L" subtitle="Closed + open P&L at mark, sampled hourly" legend={legend} testId="optionsim-equity-chart">
            <LineChart data={chart.rows} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
              <CartesianGrid {...grid} />
              <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={t => axisDate(t, chart.span)} minTickGap={40} {...axis} />
              <YAxis tickFormatter={inrShort} width={64} {...axis} />
              <ReferenceLine y={0} stroke={C.chartGrid} />
              <Tooltip {...tooltip} labelFormatter={(t: any) => `${dateTimeUtc(Number(t))} UTC`}
                formatter={(v: any, name: any) => [signedMoney(Number(v) / fx, fx), name]} />
              {visible.map(s => (
                <Line key={s.key} name={s.label} dataKey={`p${slotOf(s.key)}`} stroke={seriesColor(C.chartSeries, slotOf(s.key))} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              ))}
            </LineChart>
          </ChartCard>
          <ChartCard title="Drawdown" subtitle="Fall of cumulative P&L from its running peak" legend={legend} testId="optionsim-drawdown-chart">
            <LineChart data={chart.rows} margin={{ top: 6, right: 8, bottom: 0, left: 4 }}>
              <CartesianGrid {...grid} />
              <XAxis dataKey="t" type="number" domain={['dataMin', 'dataMax']} tickFormatter={t => axisDate(t, chart.span)} minTickGap={40} {...axis} />
              <YAxis tickFormatter={inrShort} width={64} {...axis} />
              <Tooltip {...tooltip} labelFormatter={(t: any) => `${dateTimeUtc(Number(t))} UTC`}
                formatter={(v: any, name: any) => [signedMoney(Number(v) / fx, fx), name]} />
              {visible.map(s => (
                <Line key={s.key} name={s.label} dataKey={`d${slotOf(s.key)}`} stroke={seriesColor(C.chartSeries, slotOf(s.key))} strokeWidth={2} dot={false} isAnimationActive={false} connectNulls />
              ))}
            </LineChart>
          </ChartCard>
        </Box>
      )}

      {/* ── focused strategy ── */}
      {focus && m && (
        <Card data-testid="optionsim-focus">
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
              <SeriesKey color={seriesColor(C.chartSeries, slotOf(focus.key))} label="" />
              <Typography variant="subtitle1" fontWeight={800} sx={{ overflowWrap: 'anywhere' }}>{focus.label}</Typography>
              <Chip size="small" variant="outlined" label={`${focus.costScenario} costs`} />
              {visible.length > 1 && <Typography variant="caption" color="text.secondary">{phone ? 'Tap another strategy above to see it here.' : 'Pick another strategy in the table above.'}</Typography>}
            </Stack>
            {!!m.capitalExhaustedCycles && (
              <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="optionsim-exhausted">{exhaustedText(m.capitalExhaustedCycles, m.n, m.firstExhaustedCycle)}</Alert>
            )}
            <StatGrid>
              <Stat label="Total net" value={signedMoney(m.totalNetUsd, fx)} tone={m.totalNetUsd >= 0 ? 'bull' : 'bear'} hint="After fees and modelled slippage." />
              <Stat label="Before costs" value={signedMoney(m.totalGrossUsd, fx)} hint="P&L at mark before fees and slippage." />
              <Stat label="Fees" value={money(m.feesUsd, fx)} hint="Delta option fee (min of 0.01% of index, 3.5% of premium) + 18% GST, plus settlement fees." />
              <Stat label="Slippage" value={money(m.slippageUsd, fx)} hint="Modelled half-spread and depth cost against the mark." />
              <Stat label="Median / cycle" value={signedMoney(m.medianUsd, fx)} />
              <Stat label="Win rate" value={pct(m.winRatePct, 0)} hint={`Avg win ${signedMoney(m.avgWinUsd, fx)} · avg loss ${signedMoney(m.avgLossUsd, fx)}`} />
              <Stat label="Largest win" value={signedMoney(m.largestWinUsd, fx)} />
              <Stat label="Largest loss" value={signedMoney(m.largestLossUsd, fx)} tone={m.largestLossUsd < 0 ? 'bear' : null} />
              <Stat label="Profit factor" value={m.profitFactor == null ? '—' : num(m.profitFactor, 2)} hint="Gross wins / gross losses." />
              <Stat label="ES95 / cycle" value={signedMoney(m.es95Usd, fx)} hint="Average of the worst 5% of cycles." />
              <Stat label="Max drawdown" value={signedMoney(m.maxDrawdownUsd, fx)} tone={m.maxDrawdownUsd < 0 ? 'bear' : null} />
              <Stat label="Worst open MTM" value={signedMoney(m.worstCycleMtmUsd, fx)} tone={m.worstCycleMtmUsd < 0 ? 'bear' : null} hint="Deepest mark-to-market inside any cycle, before it closed." />
              <Stat label="Stops" value={num(m.stops, 0)} hint={m.recoveredPct == null ? 'Stop-outs.' : `Of the cycles that were at one credit or more under water, ${pct(m.recoveredPct, 0)} recovered to ≥ 0.`} />
              <Stat label="Adjustments" value={`${num(m.adjustmentsPerCycle, 1)} / cycle`} hint={m.avgAdjustmentCostUsd == null ? 'No counted adjustments.' : `Average cost ${money(m.avgAdjustmentCostUsd, fx)} each.`} />
              <Stat label="Avg max margin" value={utilPct(m.avgMaxMarginUtilPct)} hint={`Mean of each cycle's highest margin use. ${UTIL_HINT}`} />
              <Stat label="Margin breaches" value={pct(m.marginBreachCyclePct, 0)} tone={m.marginBreachCyclePct > 0 ? 'warn' : null} hint="Cycles where margin use crossed the limit." />
              <Stat label="Return on max IM" value={m.returnOnMaxImPct == null ? '—' : pct(m.returnOnMaxImPct, 1)} />
              <Stat label="Without best cycle" value={signedMoney(m.totalExclBestUsd, fx)} hint={`Without the two best: ${signedMoney(m.totalExclTwoBestUsd, fx)}. A result that rests on one cycle is fragile.`} />
            </StatGrid>

            {bars.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <ChartCard title="P&L by cycle" subtitle="Each bar is one basket, in entry order" height={200} testId="optionsim-cycle-chart">
                  <BarChart data={bars} margin={{ top: 6, right: 8, bottom: 0, left: 4 }} barCategoryGap={2}>
                    <CartesianGrid {...grid} />
                    <XAxis dataKey="cycle" {...axis} minTickGap={12} />
                    <YAxis tickFormatter={inrShort} width={64} {...axis} />
                    <ReferenceLine y={0} stroke={C.chartGrid} />
                    <Tooltip {...tooltip} cursor={{ fill: C.chartGrid, fillOpacity: 0.25 }}
                      labelFormatter={(c: any, p: any) => `Cycle ${c}${p?.[0]?.payload?.t ? ` · ${dateUtc(p[0].payload.t)}` : ''}`}
                      formatter={(v: any) => [signedMoney(Number(v) / fx, fx), 'P&L']} />
                    <Bar dataKey="pnl" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                      {bars.map(b => <Cell key={b.cycle} fill={b.pnl >= 0 ? C.chartBull : C.chartBear} />)}
                    </Bar>
                  </BarChart>
                </ChartCard>
              </Box>
            )}

            {Object.keys(focus.regimes || {}).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>By path regime</Typography>
                {phone ? (
                  <Stack spacing={0.75} data-testid="optionsim-regimes">
                    {Object.entries(focus.regimes).sort((a, b) => b[1].n - a[1].n).map(([k, r]) => (
                      <Box key={k} sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" fontWeight={700} component="div">{REGIME_LABEL[k] ?? k} · {num(r.n, 0)} cycle{r.n === 1 ? '' : 's'}</Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          mean <Box component="span" sx={{ color: tone(r.meanUsd) }}>{signedMoney(r.meanUsd, fx)}</Box> · worst <Box component="span" sx={{ color: tone(r.worstUsd) }}>{signedMoney(r.worstUsd, fx)}</Box> · total <Box component="span" sx={{ color: tone(r.totalUsd) }}>{signedMoney(r.totalUsd, fx)}</Box>
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.4, fontSize: '0.75rem', whiteSpace: 'nowrap' }, '& td': { fontVariantNumeric: 'tabular-nums' } }}>
                    <TableHead><TableRow>{['Regime', 'Cycles', 'Mean', 'Worst', 'Total'].map(h => <TableCell key={h} sx={{ fontWeight: 700 }} align={h === 'Regime' ? 'left' : 'right'}>{h}</TableCell>)}</TableRow></TableHead>
                    <TableBody>
                      {Object.entries(focus.regimes).sort((a, b) => b[1].n - a[1].n).map(([k, r]) => (
                        <TableRow key={k}>
                          <TableCell>{REGIME_LABEL[k] ?? k}</TableCell>
                          <TableCell align="right">{num(r.n, 0)}</TableCell>
                          <TableCell align="right" sx={{ color: tone(r.meanUsd) }}>{signedMoney(r.meanUsd, fx)}</TableCell>
                          <TableCell align="right" sx={{ color: tone(r.worstUsd) }}>{signedMoney(r.worstUsd, fx)}</TableCell>
                          <TableCell align="right" sx={{ color: tone(r.totalUsd) }}>{signedMoney(r.totalUsd, fx)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
                )}
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>Cycles ({num(focusCycles.length, 0)})</Typography>
              {phone ? (
                <Stack spacing={0.75} data-testid="optionsim-cycle-list">
                  {focusCycles.map(c => (
                    <Stack key={c.cycle} direction="row" spacing={1} alignItems="center" sx={{ p: 0.75, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" fontWeight={800} component="div">#{c.cycle} · {dateUtc(c.summary.entryT)} · <Box component="span" sx={{ color: tone(c.summary.pnlUsd) }}>{signedMoney(c.summary.pnlUsd, fx)}</Box></Typography>
                        <Typography variant="caption" color="text.secondary" component="div" noWrap>
                          {num(c.summary.adjustments, 0)} adj · {closeLabel(c.summary)}{c.summary.equityExhausted ? ' · capital exhausted' : ''} · path {num(c.summary.minPct, 1)}% / +{num(c.summary.maxPct, 1)}%
                        </Typography>
                      </Box>
                      <Button size="small" variant="outlined" onClick={() => onReplay(c.key, c.cycle, focus.label)} sx={{ flexShrink: 0 }}>Replay</Button>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Box sx={{ overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
                  <Table size="small" stickyHeader data-testid="optionsim-cycle-table" sx={{ '& td, & th': { px: 1, py: 0.4, fontSize: '0.75rem', whiteSpace: 'nowrap' }, '& td': { fontVariantNumeric: 'tabular-nums' } }}>
                    <TableHead>
                      <TableRow>
                        {['#', 'Entry (UTC)', 'Expiry', 'Spot', 'Path min / max', 'Credit', 'P&L', 'Adj', 'Exit', 'Worst MTM', 'Max margin', ''].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700 }} align={['#', 'Entry (UTC)', 'Expiry', 'Exit', ''].includes(h) ? 'left' : 'right'}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {focusCycles.map(c => {
                        const x = c.summary;
                        return (
                          <TableRow key={c.cycle} hover>
                            <TableCell>{x.cycle}</TableCell>
                            <TableCell>{dateTimeUtc(x.entryT)}</TableCell>
                            <TableCell>{dateUtc(x.expiry)}</TableCell>
                            <TableCell align="right">${num(x.entrySpot, 0)}</TableCell>
                            <TableCell align="right">{num(x.minPct, 1)}% / +{num(x.maxPct, 1)}%</TableCell>
                            <TableCell align="right">{money(x.creditUsd, fx)}</TableCell>
                            <TableCell align="right" sx={{ color: tone(x.pnlUsd), fontWeight: 800 }}>{signedMoney(x.pnlUsd, fx)}</TableCell>
                            <TableCell align="right">{num(x.adjustments, 0)}</TableCell>
                            <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{closeLabel(x)}</TableCell>
                            <TableCell align="right" sx={{ color: tone(x.worstMtmUsd) }}>{signedMoney(x.worstMtmUsd, fx)}</TableCell>
                            <TableCell align="right" sx={x.equityExhausted ? { color: C.warn, fontWeight: 700 } : undefined}>{x.equityExhausted ? 'exhausted' : utilPct(x.maxMarginUtilPct)}</TableCell>
                            <TableCell><Button size="small" startIcon={<ReplayIcon fontSize="small" />} onClick={() => onReplay(c.key, c.cycle, focus.label)} data-testid={`optionsim-replay-${x.cycle}`}>Replay</Button></TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
              {!loading && !focusCycles.length && <Typography variant="body2" color="text.secondary">No cycles: no basket was opened in this window.</Typography>}
            </Box>

            {ex && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1.5 }}>
                Fills are modelled against Delta MARK candles (no historical bid/ask exists): spread model {ex.spread}, depth {ex.depth}{ex.compat ? `, compat ${ex.compat}` : ''}, {ex.marginMode === 'PM' ? 'PM-shaped margin [A]' : 'cross margin'}.
                {' '}{num(focus.stats.instants, 0)} decision instants · {num(focus.stats.fills, 0)} fills · {num(focus.stats.decisions, 0)} decisions{focus.stats.skippedEntries ? ` · ${num(focus.stats.skippedEntries, 0)} entries skipped` : ''}.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

const MiniStat: React.FC<{ k: string; v: string; color?: string }> = ({ k, v, color }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ fontSize: '0.62rem' }}>{k}</Typography>
    <Typography variant="caption" fontWeight={800} component="div" noWrap sx={{ color, fontVariantNumeric: 'tabular-nums' }}>{v}</Typography>
  </Box>
);

export default SimResults;
